import { captureRemixErrorBoundaryError } from '@sentry/remix';
import * as Sentry from '@sentry/remix';
import type { LinksFunction } from 'react-router';
import posthog from 'posthog-js';
import superjson from 'superjson';

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
  useNavigation,
} from 'react-router';

import { TooltipProvider } from './components/ui/tooltip';
import { Button } from './components/ui/button';

// Required CSS import, unless you're overriding the styling
import '@knocklabs/react/dist/index.css';

import styles from './globals.css?url';
import { currentUser } from './utils/auth';
import { requireAuth, isPublicRoute } from './utils/auth-check';
import { Toaster } from './components/ui/toaster';
import { CurrentUserProvider } from './contexts/CurrentUserContext';
import { IssueNavigationProvider } from './contexts/IssueNavigationContext';
import { useEffect, useState, useRef } from 'react';
import { UpdateRouteWatcher } from './routes/update';
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
} from '@trpc/client';
import { AppRouter } from 'types/trpc';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRPCProvider } from 'utils/trpc-client-helper';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'icon', href: '/favicon.ico' },
  {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com',
  },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
];

export async function loader({ request }: { request: Request }) {
  // Check if current route requires authentication
  const url = new URL(request.url);

  const user = await currentUser(request);

  return {
    user,
    isDevelopment: process.env.NODE_ENV === 'development',
  };
}

export const meta = () => [
  {
    charset: 'utf-8',
    title: `${process.env.NODE_ENV === 'development' ? '[DEV] ' : ''}Sunset`,
    viewport: 'width=device-width,initial-scale=1',
  },
];

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

function App() {
  const { user } = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const [showWarning, setShowWarning] = useState<boolean>(true);
  const timerRef = useRef<number | null>(null);

  const apiUrl = import.meta.env.VITE_NODE_API_URL;
  // Direct connection to Node API tRPC endpoint
  const url = `${apiUrl}/trpc`;
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        // Route upload mutations through non-batched httpLink to avoid cancellation/overwrites during concurrency
        splitLink({
          condition(op) {
            return op.type === 'mutation' && op.path.startsWith('upload.');
          },
          true: httpLink({
            url,
            transformer: superjson,
            fetch(url, options) {
              return fetch(url, {
                ...options,
                credentials: 'include',
              });
            },
          }),
          false: httpBatchLink({
            url,
            transformer: superjson,
            fetch(url, options) {
              return fetch(url, {
                ...options,
                credentials: 'include',
              });
            },
          }),
        }),
      ],
    })
  );

  // Initialize PostHog
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'development' &&
      process.env.NODE_ENV !== 'test'
    ) {
      posthog.init('phc_AOyOBE3maVGa1GgIg6y9D2pJl1EHY1wkSpioM9cZZVv', {
        api_host: 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        session_recording: {
          maskAllInputs: false,
        },
      });
    }
  }, []);

  // Store user info in localStorage for error boundary use
  useEffect(() => {
    if (typeof window !== 'undefined' && user) {
      try {
        const userInfo = {
          user_id: user.user_id,
          name: user.name,
          email: user.email,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('sunset_user_info', JSON.stringify(userInfo));
      } catch (error) {
        console.warn('Failed to store user info in localStorage:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    try {
      if (user) {
        posthog.identify(user.user_id, { email: user.email, name: user.name });
      }
    } catch (error) {
      console.error('Error identifying user:', error);
    }
  }, [user]);

  // Clear any existing timer when component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    setShowWarning(false);

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set a new timer to show the warning again after 30 seconds
    timerRef.current = window.setTimeout(() => {
      setShowWarning(true);
      timerRef.current = null;
    }, 30000);
  };

  return (
    <html>
      <head>
        <link rel="icon" href="data:image/x-icon;base64,AA" />
        <Meta />
        <Links />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <CurrentUserProvider
              value={user ? { ...user, id: user.user_id } : null}
            >
              <IssueNavigationProvider>
                <TooltipProvider>
                  <main className="flex flex-col h-screen">
                    {navigation.state !== 'idle' && (
                      <div className="fixed top-0 left-0 right-0 z-50">
                        <div className="h-[2px] w-full bg-gray-100/20">
                          <div className="h-full w-[20%] bg-blue-400 animate-loader will-change-[width] origin-left" />
                        </div>
                      </div>
                    )}
                    {showWarning && (
                      <div
                        id="production-warning"
                        className="hidden h-[100vh] w-[100vw] border-[8px] border-yellow-500 fixed top-0 left-0 z-[1000] pointer-events-none"
                      >
                        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                          <div className="h-full w-[100%] bg-yellow-500/20 animate-sweep will-change-transform pointer-events-none" />
                        </div>
                        <div className="text-center text-white bg-yellow-500 p-2 rounded-b-lg fixed top-0 left-1/2 -translate-x-1/2 z-[1000] flex items-center justify-center">
                          WARNING: You are viewing the production environment.
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2 bg-yellow-600 hover:bg-yellow-700 text-white pointer-events-auto"
                            onClick={handleDismiss}
                          >
                            Dismiss for 30s
                          </Button>
                        </div>
                      </div>
                    )}
                    <UpdateRouteWatcher />
                    <Outlet />
                  </main>
                  <ScrollRestoration />
                  <Scripts />
                  <Toaster />
                </TooltipProvider>
              </IssueNavigationProvider>
            </CurrentUserProvider>
          </TRPCProvider>
        </QueryClientProvider>
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();

  // Show detailed error in development
  if (process.env.NODE_ENV === 'development') {
    return (
      <html>
        <head>
          <title>Error | Sunset</title>
          <Meta />
          <Links />
        </head>
        <body className="p-8 bg-red-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-red-600 mb-4">
              Development Error
            </h1>

            {isRouteErrorResponse(error) ? (
              <div className="bg-white p-6 rounded-lg shadow-lg mb-4">
                <h2 className="text-xl font-semibold mb-2">
                  {error.status} {error.statusText}
                </h2>
                <pre className="bg-gray-100 p-4 rounded overflow-auto text-sm">
                  {JSON.stringify(error.data, null, 2)}
                </pre>
              </div>
            ) : error instanceof Error ? (
              <div className="bg-white p-6 rounded-lg shadow-lg mb-4">
                <h2 className="text-xl font-semibold mb-2 text-red-600">
                  {error.message}
                </h2>
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Stack Trace:</h3>
                  <pre className="bg-gray-100 p-4 rounded overflow-auto text-xs whitespace-pre-wrap">
                    {error.stack}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-lg mb-4">
                <h2 className="text-xl font-semibold mb-2">Unknown Error</h2>
                <pre className="bg-gray-100 p-4 rounded overflow-auto text-sm">
                  {JSON.stringify(error, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                onClick={() => window.location.reload()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Reload Page
              </Button>
              <Button variant="outline" onClick={() => window.history.back()}>
                Go Back
              </Button>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  // Only disable Sentry error boundary for test environment
  if (process.env.NODE_ENV !== 'test') {
    // Capture the error and get the event ID for user feedback
    captureRemixErrorBoundaryError(error);

    return (
      <html>
        <head>
          <title>Something went wrong | Sunset</title>
          <Meta />
          <Links />
        </head>
        <body>
          <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4">
            <div className="max-w-lg w-full text-center">
              {/* Error Icon */}

              {/* Main Message */}
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Oops! Something went wrong
              </h1>

              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                We encountered an unexpected error. Don't worry, our team has
                been automatically notified and we're working on it.
              </p>

              {/* Action Buttons */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => window.location.reload()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Try again
                  </Button>

                  <a
                    href="/"
                    className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Go to homepage
                  </a>
                </div>

                <Button
                  variant="ghost"
                  onClick={() => window.history.back()}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  ← Go back
                </Button>
              </div>

              {/* Additional Help */}
              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Need help? Here are ways to reach us.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm">
                  <a
                    href="mailto:support@sunset.com"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    Email Support
                  </a>
                  <span className="hidden sm:inline text-gray-300 dark:text-gray-600">
                    •
                  </span>

                  <a
                    href="https://sunset.betteruptime.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    System Status
                  </a>
                </div>
              </div>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  } else {
    const error = useRouteError();

    if (isRouteErrorResponse(error)) {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    } else if (error instanceof Error) {
      return (
        <div>
          <h1>Error</h1>
          <p>{error.message}</p>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </div>
      );
    } else {
      return <h1>Unknown Error</h1>;
    }
  }
};
export default App;
